/**
 * <recently-viewed>
 *
 * Tracks every product the customer visits in localStorage (LRU, capped at
 * 12 handles) and, on PDPs that mount this element, renders a horizontal
 * card strip of their previously viewed products (excluding the current one).
 *
 * Pure client-side — no metafields, no admin setup, no apps. Hides itself
 * when the strip would have fewer than 2 cards.
 *
 * Data shape (localStorage key 'yxe:recently_viewed'):
 *   ["handle-of-most-recent", "handle-2", ..., "handle-12"]
 *   Most recent first. Capped at 12 entries.
 */

(function () {
  const STORAGE_KEY = 'yxe:recently_viewed';
  const MAX_STORED = 12;
  const DEFAULT_VISIBLE = 6;

  function getCurrentHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
    } catch (e) {
      /* quota exceeded or storage disabled — silently no-op */
    }
  }

  function track(handle) {
    if (!handle) return;
    const list = read();
    const next = [handle, ...list.filter((h) => h !== handle)];
    write(next);
  }

  // Track the current PDP on script load (before the element even mounts —
  // so customers who don't see the strip still build up history)
  const current = getCurrentHandle();
  if (current) track(current);

  /* -------- <recently-viewed> web component -------- */

  class RecentlyViewed extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.visible = parseInt(this.dataset.maxVisible, 10) || DEFAULT_VISIBLE;
      this.listEl = this.querySelector('[data-recently-viewed-list]');
      if (!this.listEl) return;

      const handles = read().filter((h) => h !== getCurrentHandle()).slice(0, this.visible);

      if (handles.length < 2) {
        // Strip needs at least 2 to be interesting; hide entirely otherwise.
        this.setAttribute('hidden', '');
        return;
      }

      this.fetchAndRender(handles);
    }

    fetchAndRender(handles) {
      // Fetch each product's JSON in parallel. Any that 404 (deleted product)
      // are quietly dropped from the strip.
      Promise.all(
        handles.map((handle) =>
          fetch(`/products/${handle}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      ).then((products) => {
        const valid = products.filter((p) => p && p.handle);

        if (valid.length < 2) {
          this.setAttribute('hidden', '');
          return;
        }

        const fragment = document.createDocumentFragment();
        for (const product of valid) {
          fragment.appendChild(this.renderCard(product));
        }
        this.listEl.appendChild(fragment);
        this.removeAttribute('hidden');
      });
    }

    renderCard(product) {
      const li = document.createElement('li');
      li.className = 'rv-card-wrap';

      const a = document.createElement('a');
      a.className = 'rv-card';
      a.href = product.url || `/products/${product.handle}`;

      const imgUrl = product.featured_image
        ? product.featured_image.replace(/\.(jpg|jpeg|png|webp)/i, '_400x.$1')
        : null;

      a.innerHTML = `
        <span class="rv-card__media">
          ${
            imgUrl
              ? `<img class="rv-card__image" src="${imgUrl}" alt="${escapeHtml(
                  product.title || ''
                )}" loading="lazy">`
              : '<span class="rv-card__placeholder" aria-hidden="true"></span>'
          }
        </span>
        <span class="rv-card__body">
          <span class="rv-card__title">${escapeHtml(product.title || '')}</span>
          <span class="rv-card__price">${formatMoney(product.price)}</span>
        </span>
      `;

      li.appendChild(a);
      return li;
    }
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[c];
    });
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    const dollars = (cents / 100).toFixed(2);
    // Read shop currency format if available; otherwise default to $.
    const format = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
    return format.replace(/\{\{\s*amount\s*\}\}/g, dollars);
  }

  if (!customElements.get('recently-viewed')) {
    customElements.define('recently-viewed', RecentlyViewed);
  }
})();
